import * as React from 'react';
import { AppContext } from '../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import useGlynt from '../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../errorResponses';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import useDeleteVendor from '../hooks/vendors/useDeleteVendor';

export interface IVendorDeleteProps {
  vendorId: number;
  vendorName: string;
}

const VendorDelete: React.FC<IVendorDeleteProps> = ({ vendorId, vendorName }) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const theme = useTheme();

  const deleteVendorMutation = useDeleteVendor({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Vendor deleted successfully.',
      });
    },
    onError: (error: any) => {
      const apiErrors = error?.response?.data?.non_field_errors;
      const detailError = error?.response?.data?.detail;
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors[0]) ||
        detailError ||
        error?.message ||
        'Failed to delete vendor. Please try again.';

      setToast({
        variant: 'error',
        open: true,
        message: errorMessage,
      });
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    deleteVendorMutation.mutate(vendorId);
    setOpen(false);
  };

  return (
    <>
      <Tooltip placement="top" title={`Delete Vendor`} disableFocusListener disableTouchListener>
        <IconButton
          data-cy={`btn-delete-vendor-${vendorName}`}
          onClick={!deleteVendorMutation.isPending ? handleClickOpen : undefined}
          disabled={deleteVendorMutation.isPending}
          sx={{
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.main + '20', // 20% opacity for hover
            },
          }}
        >
          <DeleteIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={deleteVendorMutation.isPending}
        cypressId="dialog-vendor-delete"
      >
        <DialogTitleGlynt
          title="Confirmation"
          closeModal={handleCloseConfirmation}
          loading={deleteVendorMutation.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            Are you sure you want to delete vendor "{vendorName}"?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleDelete}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-vendor-delete-modal-cancel"
          submitCypressId="btn-vendor-delete-modal-yes"
          clearCypressId=""
          submitButtonText="Yes"
          submitLoading={deleteVendorMutation.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default VendorDelete;
